/**
 * Module-level timezone source of truth.
 *
 * Read by axios interceptors and RTK Query base wrappers (which can't read
 * Redux state synchronously without coupling), written by the timezone slice
 * on every change. Also keeps dayjs's default in sync so any `dayjs.tz()` call
 * across the app picks up the user's current selection.
 *
 * Extends the dayjs utc + timezone plugins inline because the timezoneSlice
 * (which imports this module) is initialized at store-construction time,
 * before utils/dates.ts may have run. Without this, `dayjs.tz` is undefined
 * on first access.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const FALLBACK_TZ = 'America/New_York';

const detectBrowserTz = (): string => {
    try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return detected || FALLBACK_TZ;
    } catch {
        return FALLBACK_TZ;
    }
};

let currentTz: string = detectBrowserTz();
dayjs.tz.setDefault(currentTz);

export const getCurrentTz = (): string => currentTz;

export const setCurrentTz = (tz: string): void => {
    currentTz = tz;
    dayjs.tz.setDefault(tz);
};

export const getDetectedTz = detectBrowserTz;
export const TZ_FALLBACK = FALLBACK_TZ;
